import React, { useState, useEffect } from 'react';
import { getFaviconUrl, getFaviconUrlSync } from '@/lib/favicon';

interface FaviconProps {
  domain: string;
  size?: number;
  className?: string;
  fallbackText?: string;
  showLoading?: boolean;
  onLoad?: (url: string) => void;
  onError?: (domain: string) => void;
  highRes?: boolean;
}

export function Favicon({
  domain,
  size = 32,
  className = '',
  fallbackText,
  showLoading = true,
  onLoad,
  onError,
  highRes = false
}: FaviconProps) {
  const [faviconUrl, setFaviconUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const cleanDomain = domain.replace(/^www\./, '').toLowerCase();
  const displayText = fallbackText || cleanDomain.charAt(0).toUpperCase();

  useEffect(() => {
    let isMounted = true;
    const loadFavicon = async () => {
      if (!domain) return;
      setIsLoading(true);
      setHasError(false);
      try {
        // Start with Brandfetch for immediate display
        const syncUrl = getFaviconUrlSync(cleanDomain, highRes);
        if (isMounted) {
          setFaviconUrl(syncUrl);
        }
        // Then try to get a better favicon asynchronously (if needed)
        const betterUrl = await getFaviconUrl(cleanDomain, false, highRes);
        if (isMounted && betterUrl !== syncUrl) {
          setFaviconUrl(betterUrl);
          onLoad?.(betterUrl);
        }
      } catch (error) {
        console.warn(`Failed to load favicon for ${domain}:`, error);
        if (isMounted) {
          setHasError(true);
          onError?.(domain);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    loadFavicon();
    return () => {
      isMounted = false;
    };
  }, [domain, cleanDomain, onLoad, onError, highRes]);

  const handleImageError = () => {
    setHasError(true);
    onError?.(domain);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setHasError(false);
    setIsLoading(true);
    setFaviconUrl(getFaviconUrlSync(cleanDomain, highRes) + `&retry=${retryCount + 1}`);
  };

  const baseClasses = `rounded-md border bg-muted flex items-center justify-center text-white text-xs font-bold`;
  const sizeClasses = `w-${size / 4} h-${size / 4}`;

  if (isLoading && showLoading) {
    return (
      <div 
        className={`${baseClasses} ${sizeClasses} ${className} animate-pulse bg-muted`}
        style={{ width: size, height: size }}
      >
        <div className="w-3 h-3 bg-muted-foreground/20 rounded animate-pulse" />
      </div>
    );
  }

  if (hasError || !faviconUrl) {
    return (
      <div 
        className={`${baseClasses} ${sizeClasses} ${className} bg-gradient-to-br from-primary to-primary/60`}
        style={{ width: size, height: size }}
        title={`${domain} favicon`}
        onClick={handleRetry}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleRetry()}
      >
        {displayText}
      </div>
    );
  }

  return (
    <div className="relative">
      <img
        src={faviconUrl}
        alt={`${domain} favicon`}
        className={`${sizeClasses} ${className} rounded-md border bg-muted`}
        style={{ width: size, height: size }}
        onError={handleImageError}
        onLoad={() => {
          setIsLoading(false);
          onLoad?.(faviconUrl);
        }}
        title={`${domain} favicon`}
      />
      {hasError && (
        <div 
          className={`absolute inset-0 ${baseClasses} bg-gradient-to-br from-primary to-primary/60 cursor-pointer`}
          style={{ width: size, height: size }}
          onClick={handleRetry}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleRetry()}
          title="Click to retry loading favicon"
        >
          {displayText}
        </div>
      )}
    </div>
  );
}

// Optimized version for lists (no loading states, immediate fallback)
export function FaviconSimple({
  domain,
  size = 32,
  className = '',
  highRes = false
}: Omit<FaviconProps, 'showLoading' | 'onLoad' | 'onError'>) {
  const [hasError, setHasError] = useState(false);
  const cleanDomain = domain.replace(/^www\./, '').toLowerCase();
  const displayText = cleanDomain.charAt(0).toUpperCase();
  const faviconUrl = getFaviconUrlSync(cleanDomain, highRes);

  if (hasError) {
    return (
      <div 
        className={`w-${size / 4} h-${size / 4} rounded-md bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-xs font-bold border ${className}`}
        style={{ width: size, height: size }}
        title={`${domain} favicon`}
      >
        {displayText}
      </div>
    );
  }

  return (
    <img
      src={faviconUrl}
      alt={`${domain} favicon`}
      className={`w-${size / 4} h-${size / 4} rounded-md border bg-muted ${className}`}
      style={{ width: size, height: size }}
      onError={() => setHasError(true)}
      title={`${domain} favicon`}
    />
  );
}

// Specialized component for backlinks with enhanced reliability
export function BacklinkFavicon({
  domain,
  size = 32,
  className = ''
}: {
  domain: string;
  size?: number;
  className?: string;
}) {
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  const cleanDomain = domain.replace(/^www\./, '').toLowerCase();
  const displayText = cleanDomain.charAt(0).toUpperCase();

  // Fallback services in order of preference - Updated with larger sizes
  const fallbackServices = [
    `https://cdn.brandfetch.io/${cleanDomain}/w/128/h/128?c=1iddr807FNSCfhcKGsr`,
    `https://cdn.brandfetch.io/${cleanDomain}/w/256/h/256?c=1iddr807FNSCfhcKGsr`,
    `https://cdn.brandfetch.io/${cleanDomain}/w/400/h/400?c=1iddr807FNSCfhcKGsr`,
    `https://favicon.is/${cleanDomain}`,
    `https://icons.duckduckgo.com/ip3/${cleanDomain}.ico`,
    `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=32`,
    `https://favicon.cc/favicon/${cleanDomain}`,
  ];

  useEffect(() => {
    const loadFavicon = async () => {
      setHasError(false);
      setCurrentUrl(fallbackServices[0]);
      if (retryCount > 0) {
        const serviceIndex = Math.min(retryCount, fallbackServices.length - 1);
        setCurrentUrl(fallbackServices[serviceIndex]);
      }
    };
    loadFavicon();
  }, [domain, retryCount]);

  const handleImageError = () => {
    if (retryCount < fallbackServices.length - 1) {
      setRetryCount(prev => prev + 1);
    } else {
      setHasError(true);
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    setHasError(false);
  };

  if (hasError) {
    return (
      <div 
        className={`w-${size / 4} h-${size / 4} rounded-md bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-xs font-bold border ${className} cursor-pointer hover:opacity-80 transition-opacity`}
        style={{ width: size, height: size }}
        title={`${domain} favicon - Click to retry`}
        onClick={handleRetry}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleRetry()}
      >
        {displayText}
      </div>
    );
  }

  return (
    <img
      src={currentUrl}
      alt={`${domain} favicon`}
      className={`w-${size / 4} h-${size / 4} rounded-md border bg-muted ${className}`}
      style={{ width: size, height: size }}
      onError={handleImageError}
      title={`${domain} favicon`}
    />
  );
} 